import {
  AntdLayout,
  Button,
  Card,
  Col,
  Form,
  Grid,
  Input,
  Row,
  Tabs,
  Typography,
  notification,
} from '@pankod/refine-antd';
import eloBooksDetailed from 'assets/images/elo-books-details-logo.svg';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

type OnboardingStep = 'login' | 'user';
type LoginValues = { email: string; password: string };
type UserValues = { name: string; email: string; password: string };

const CLOUDSIGHT_TOKEN_KEY = 'cloudsight_access_token';
const ONBOARDING_ROUTES = {
  login: '/onboarding',
  user: '/onboarding/user',
} as const;

const getErrorMessage = (data: unknown, fallback: string) => {
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message;
  }

  return fallback;
};

const StepLabel: React.FC<{ active: boolean; label: string }> = ({ active, label }) => (
  <div style={{ padding: '6px 0' }}>
    <Typography.Text
      strong
      style={{
        display: 'block',
        color: active ? '#0f172a' : '#94a3b8',
      }}
    >
      {label}
    </Typography.Text>
  </div>
);

export const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [loginForm] = Form.useForm<LoginValues>();
  const [userForm] = Form.useForm<UserValues>();
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [sessionAccessToken, setSessionAccessToken] = useState<string | null>(null);

  const canAccessUserStep = Boolean(sessionAccessToken);
  const activeStep: OnboardingStep =
    location.pathname === ONBOARDING_ROUTES.user && canAccessUserStep ? 'user' : 'login';

  useEffect(() => {
    window.localStorage.removeItem(CLOUDSIGHT_TOKEN_KEY);
  }, []);

  const changeStep = (step: OnboardingStep) => {
    if (step === 'user' && !canAccessUserStep) {
      return;
    }

    navigate(step === 'login' ? ONBOARDING_ROUTES.login : ONBOARDING_ROUTES.user, {
      replace: true,
    });
  };

  const handleLoginSubmit = async (values: LoginValues) => {
    const baseUrl = process.env.REACT_APP_CLOUDSIGHT_API_BASE_URL;
    const clientId = process.env.REACT_APP_CLOUDSIGHT_CLIENT_ID;

    if (!baseUrl || !clientId) {
      notification.error({
        message: 'Cloudsight Login failed',
        description: 'Missing Cloudsight API configuration in environment variables.',
      });
      return;
    }

    const url = `${baseUrl}/auth/login?response_type=token&client_id=${clientId}`;

    setLoginSubmitting(true);
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: values.email,
          password: values.password,
        }).toString(),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(getErrorMessage(data, 'Signup failed'));
      }

      if (!data?.access_token) {
        throw new Error('Login succeeded but no access token was returned.');
      }

      window.localStorage.setItem(CLOUDSIGHT_TOKEN_KEY, data.access_token);
      setSessionAccessToken(data.access_token);
      userForm.resetFields();
      navigate(ONBOARDING_ROUTES.user, { replace: true });
    } catch (err: any) {
      notification.error({
        message: 'Cloudsight Login failed',
        description: err?.message || 'Unexpected error',
      });
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleUserSubmit = async (values: UserValues) => {
    const baseUrl = process.env.REACT_APP_CLOUDSIGHT_API_BASE_URL;
    const service = process.env.REACT_APP_CLOUDSIGHT_CLIENT_ID;

    if (!baseUrl || !service) {
      notification.error({
        message: 'Create user failed',
        description: 'Missing Cloudsight API configuration in environment variables.',
      });
      return;
    }

    if (!sessionAccessToken) {
      notification.error({
        message: 'Create user failed',
        description: 'Complete the Cloudsight login step first.',
      });
      changeStep('login');
      return;
    }

    const url = `${baseUrl}/users?service=${service}`;

    setUserSubmitting(true);
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          password: values.password,
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(getErrorMessage(data, 'Create user failed'));
      }

      window.localStorage.removeItem(CLOUDSIGHT_TOKEN_KEY);
      setSessionAccessToken(null);
      userForm.resetFields();
      loginForm.resetFields(['password']);
      notification.success({
        message: 'User created',
        description: 'Onboarding is complete. Log in to continue to your account.',
      });
      navigate('/login', { replace: true });
    } catch (err: any) {
      notification.error({
        message: 'Create user failed',
        description: err?.message || 'Unexpected error',
      });
    } finally {
      setUserSubmitting(false);
    }
  };

  const CardTitle = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60px',
      }}
    >
      <img src={eloBooksDetailed} alt="Logo" style={{ height: '4rem', width: 'auto' }} />
    </div>
  );

  if (location.pathname === ONBOARDING_ROUTES.user && !canAccessUserStep) {
    return <Navigate to={ONBOARDING_ROUTES.login} replace />;
  }

  return (
    <AntdLayout
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: screens.md ? '40px 24px' : '20px 12px',
        backgroundColor: '#eff7f7',
      }}
    >
      <Row
        justify="center"
        align="middle"
        style={{
          width: '100%',
        }}
      >
        <Col xs={22} sm={18} md={14} lg={10} xl={8}>
          <Card
            style={{
              maxWidth: '400px',
              margin: 'auto',
            }}
            title={CardTitle}
          >
            <Typography.Title level={4} style={{ textAlign: 'center', marginBottom: '16px' }}>
              User Onboarding
            </Typography.Title>
            <Tabs
              activeKey={activeStep}
              centered
              onChange={(key) => changeStep(key as OnboardingStep)}
            >
              <Tabs.TabPane
                key="login"
                tab={<StepLabel active={activeStep === 'login'} label="Login Cloudsight" />}
              >
                <div style={{ marginTop: '8px' }}>
                  <Form form={loginForm} layout="vertical" onFinish={handleLoginSubmit}>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input placeholder="you@example.com" autoComplete="email" />
                    </Form.Item>

                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[{ required: true, message: 'Please enter your password' }]}
                    >
                      <Input.Password placeholder="••••••••" autoComplete="current-password" />
                    </Form.Item>

                    <Button
                      type="primary"
                      size="large"
                      block
                      htmlType="submit"
                      loading={loginSubmitting}
                      disabled={loginSubmitting}
                      style={{ height: '48px', backgroundColor: '#426DEA' }}
                    >
                      Continue to User Setup
                    </Button>
                  </Form>
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane
                key="user"
                disabled={!canAccessUserStep}
                tab={<StepLabel active={activeStep === 'user'} label="Create User" />}
              >
                <div style={{ marginTop: '8px' }}>
                  <Form form={userForm} layout="vertical" onFinish={handleUserSubmit}>
                    <Form.Item
                      label="Name"
                      name="name"
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input placeholder="Your name" autoComplete="name" />
                    </Form.Item>

                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' },
                      ]}
                    >
                      <Input placeholder="you@example.com" autoComplete="email" />
                    </Form.Item>

                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[{ required: true, message: 'Please enter your password' }]}
                    >
                      <Input.Password placeholder="••••••••" autoComplete="new-password" />
                    </Form.Item>

                    <Button
                      type="primary"
                      size="large"
                      block
                      htmlType="submit"
                      loading={userSubmitting}
                      disabled={userSubmitting || !canAccessUserStep}
                      style={{ height: '42px', backgroundColor: '#426DEA' }}
                    >
                      Create User
                    </Button>
                  </Form>
                </div>
              </Tabs.TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </AntdLayout>
  );
};
